/**
 * Package Service
 * Handles all package-related API calls
 * @service packageService
 */

import { config } from "../config";

// API base URL from config (config.api.baseUrl already includes /api/v1 if needed)
const API_BASE_URL = config.api.baseUrl;

/**
 * Package interface
 */
export interface Package {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  max_businesses: number;
  billing_period?: string;
  billing_interval?: number;
  target_audience: string;
  is_active: boolean;
  is_popular?: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * User package usage info interface
 */
export interface PackageUsage {
  package?: Package;
  currentUsage: number;
  maxAllowed: number;
  canCreateMore: boolean;
  expiresAt?: string;
  isExpired: boolean;
}

/**
 * Get all available packages
 * @returns Promise with packages array
 */
export const getAllPackages = async (): Promise<Package[]> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/packages`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to fetch packages with status ${response.status}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Помилка отримання пакетів:", error);
    throw error;
  }
};

/**
 * Get all active packages for user interfaces (filters out inactive packages)
 * @returns Promise with active packages array
 */
export const getActivePackages = async (): Promise<Package[]> => {
  try {
    const allPackages = await getAllPackages();
    return allPackages.filter((pkg) => pkg.is_active);
  } catch (error) {
    console.error("❌ Помилка отримання активних пакетів:", error);
    throw error;
  }
};

/**
 * Create a new package (admin only)
 * @param packageData - Package data to create
 * @returns Promise with created package
 */
export const createPackage = async (packageData: {
  name: string;
  description: string;
  price: number;
  currency: string;
  max_businesses: number;
  target_audience: string;
}): Promise<Package> => {
  console.log("🔗 createPackage API called with data:", packageData);
  console.log("🔗 API_BASE_URL:", API_BASE_URL);
  console.log("🔗 Full URL:", `${API_BASE_URL}/admin/packages`);

  try {
    const token = localStorage.getItem("auth_token");
    console.log("🔗 Auth token present:", !!token);

    const requestBody = JSON.stringify(packageData);
    console.log("🔗 Request body:", requestBody);

    const response = await fetch(`${API_BASE_URL}/admin/packages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: requestBody,
    });

    console.log("🔗 Response status:", response.status);
    console.log("🔗 Response ok:", response.ok);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("🔗 Error response data:", errorData);
      throw new Error(
        errorData.error ||
          `Failed to create package with status ${response.status}`,
      );
    }

    const result = await response.json();
    console.log("🔗 Success response:", result);
    return result;
  } catch (error) {
    console.error("❌ Помилка створення пакета:", error);
    console.error("❌ Error details:", error);
    throw error;
  }
};

/**
 * Get current user's package with usage information
 * @returns Promise with package usage info
 */
export const getUserPackageUsage = async (): Promise<PackageUsage> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/packages/user/usage`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to fetch package usage with status ${response.status}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Помилка отримання інформації про пакет:", error);
    throw error;
  }
};

/**
 * Update an existing package (admin only)
 * @param packageId - ID of the package to update
 * @param updateData - Updated package data
 * @returns Promise with updated package
 */
export const updatePackage = async (
  packageId: string,
  updateData: Partial<{
    name: string;
    description: string;
    price: number;
    currency: string;
    max_businesses: number;
    target_audience: string;
    is_active: boolean;
    is_popular: boolean;
  }>,
): Promise<Package> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(
      `${API_BASE_URL}/admin/packages/${packageId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(updateData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to update package with status ${response.status}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Помилка оновлення пакета:", error);
    throw error;
  }
};

/**
 * Assign package to user (admin only)
 * @param userId - User ID to assign package to
 * @param packageId - Package ID to assign
 * @returns Promise with assignment result
 */
export const assignPackageToUser = async (
  userId: string,
  packageId: string,
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/admin/packages/assign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        user_id: userId,
        package_id: packageId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to assign package with status ${response.status}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Помилка призначення пакета:", error);
    throw error;
  }
};

/**
 * Assign inactive package to user (no validity period) (admin only)
 * @param userId - User ID to assign package to
 * @param packageId - Package ID to assign
 * @returns Promise with assignment result
 */
export const assignInactivePackageToUser = async (
  userId: string,
  packageId: string,
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/admin/packages/assign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        user_id: userId,
        package_id: packageId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to assign inactive package with status ${response.status}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Помилка призначення неактивного пакета:", error);
    throw error;
  }
};

/**
 * Assign package to user with expiration (admin only)
 * @param userId - User ID to assign package to
 * @param packageId - Package ID to assign
 * @param expiresAt - Optional expiration date
 * @param durationDays - Optional duration in days
 * @returns Promise with assignment result
 */
export const assignPackageToUserWithExpiration = async (
  userId: string,
  packageId: string,
  expiresAt?: string,
  durationDays?: number,
): Promise<{ message: string; expiresAt?: string }> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(
      `${API_BASE_URL}/admin/packages/assign-with-expiration`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          user_id: userId,
          package_id: packageId,
          expires_at: expiresAt,
          duration_days: durationDays,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to assign package with status ${response.status}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Помилка призначення пакета з терміном:", error);
    throw error;
  }
};

/**
 * Check if user can create a new business
 * @returns Promise with business creation check result
 */
export const checkBusinessCreationLimit = async (): Promise<{
  canCreate: boolean;
  currentCount: number;
  maxAllowed: number;
  package?: Package;
}> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(
      `${API_BASE_URL}/packages/check-business-limit`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to check business limit with status ${response.status}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Помилка перевірки ліміту бізнесу:", error);
    throw error;
  }
};

/**
 * Sync business statuses with package validity (admin only)
 * Ensures that businesses are active when their packages are valid
 * @returns Promise with sync result
 */
export const syncBusinessStatuses = async (): Promise<{
  message: string;
}> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(
      `${API_BASE_URL}/admin/packages/sync-business-statuses`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to sync business statuses with status ${response.status}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Помилка синхронізації статусів бізнесу:", error);
    throw error;
  }
};
